// WP13 Gate 3 — Company discovery landing. Canonical companies are the only
// entity results; Company Type / Zone / Use Case / Blockchain / Asset Class
// are filters and context only. Ranked by published evidence count for
// presentation — never a strength or leadership ranking.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useWp12 } from "./useWp12";
import InfoTip from "./InfoTip";

const PALETTE = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#be185d", "#4d7c0f", "#b45309", "#475569"];

const Select = ({ value, onChange, options, all, testId }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="text-[11px] px-2 py-1.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface))] max-w-[180px]">
        <option value="">{all}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

// Top-N observed-activity chart — published weekly evidence counts, verbatim.
// Missing company-weeks stay missing (connectNulls=false): not zero.
const ActivityChart = ({ companies }) => {
    const ids = companies.map((c) => c.canonicalOrganizationId).join(",");
    const { status, data } = useWp12(`organizations/activity?ids=${encodeURIComponent(ids)}`);
    const [focused, setFocused] = useState(null);
    const rows = useMemo(() => {
        const series = data?.companies || [];
        const weeks = [...new Set(series.flatMap((s) => (s.weeklyTimeline || []).map((w) => w.yearWeek)))].sort();
        return {
            series,
            data: weeks.map((wk) => {
                const row = { week: wk };
                for (const s of series) {
                    const pt = (s.weeklyTimeline || []).find((w) => w.yearWeek === wk);
                    row[s.canonicalOrganizationId] = pt ? pt.evidenceCount : null;
                }
                return row;
            }),
        };
    }, [data]);
    if (!ids) return null;
    if (status === "loading") return <div className="text-[11px] text-[hsl(var(--mute))] p-4">Loading activity…</div>;
    if (!rows.series.length || rows.data.length < 2) return null;
    return (
        <div data-testid="wp13-companies-activity-chart">
            <ResponsiveContainer width="100%" height={220}>
                <LineChart data={rows.data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--line))" vertical={false} />
                    <XAxis dataKey="week" tick={{ fontSize: 9, fill: "hsl(var(--mute))" }}
                           tickFormatter={(w) => String(w).replace("2026-", "")} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "hsl(var(--mute))" }} tickLine={false} axisLine={false} width={28} />
                    <Tooltip contentStyle={{ fontSize: 10, borderRadius: 6, border: "1px solid hsl(var(--line))" }}
                             formatter={(v, key) => {
                                 const s = rows.series.find((x) => x.canonicalOrganizationId === key);
                                 return [v, s?.displayName || key];
                             }} />
                    {rows.series.map((s, i) => (
                        <Line key={s.canonicalOrganizationId} type="monotone" dataKey={s.canonicalOrganizationId}
                              stroke={PALETTE[i % PALETTE.length]}
                              strokeWidth={focused === s.canonicalOrganizationId ? 2.2 : 1.5}
                              strokeOpacity={focused && focused !== s.canonicalOrganizationId ? 0.12 : 1}
                              dot={{ r: 1.8, fillOpacity: focused && focused !== s.canonicalOrganizationId ? 0.12 : 1 }}
                              connectNulls={false} isAnimationActive={false} />
                    ))}
                </LineChart>
            </ResponsiveContainer>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-1">
                {rows.series.map((s, i) => (
                    <Link key={s.canonicalOrganizationId} to={`/market-v2/organizations/${encodeURIComponent(s.canonicalOrganizationId)}`}
                          onMouseEnter={() => setFocused(s.canonicalOrganizationId)}
                          onMouseLeave={() => setFocused(null)}
                          onFocus={() => setFocused(s.canonicalOrganizationId)}
                          onBlur={() => setFocused(null)}
                          className={`flex items-center gap-1.5 min-w-0 text-[10px] hover:text-[hsl(var(--accent-ink))] hover:underline transition-opacity ${focused && focused !== s.canonicalOrganizationId ? "opacity-40" : ""}`}
                          data-testid={`wp13-companies-chart-legend-${s.canonicalOrganizationId}`}>
                        <span className="w-3 h-0.5 rounded flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                        <span className="truncate">{s.displayName}</span>
                    </Link>
                ))}
            </div>
            <p className="text-[9px] text-[hsl(var(--mute))] mt-2">
                Published evidence activity per week. Weeks without a published observation are not drawn — a missing observation is not zero. Activity volume is observation coverage, not company strength.
            </p>
        </div>
    );
};

export default function Companies() {
    const { status, data, error } = useWp12("organizations");
    const marketRes = useWp12("market");
    const [search, setSearch] = useState("");
    const [zone, setZone] = useState("");
    const [useCase, setUseCase] = useState("");
    const [chain, setChain] = useState("");
    const [asset, setAsset] = useState("");
    const [shown, setShown] = useState(50);

    const companies = data?.companies || [];
    const zones = marketRes.data?.marketZones || [];
    const useCases = marketRes.data?.useCases || [];

    const facets = useMemo(() => {
        const chains = new Map(), assets = new Map();
        for (const c of companies) {
            for (const b of c.blockchains || []) chains.set(b, (chains.get(b) || 0) + 1);
            for (const a of c.assetClasses || []) assets.set(a, (assets.get(a) || 0) + 1);
        }
        const top = (m) => [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 30).map(([k]) => ({ value: k, label: k }));
        return { chains: top(chains), assets: top(assets) };
    }, [companies]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return companies.filter((c) =>
            (!q || (c.displayName || "").toLowerCase().includes(q)) &&
            (!zone || (c.marketZoneKeys || []).includes(zone)) &&
            (!useCase || (c.useCaseKeys || []).includes(useCase)) &&
            (!chain || (c.blockchains || []).includes(chain)) &&
            (!asset || (c.assetClasses || []).includes(asset)));
    }, [companies, search, zone, useCase, chain, asset]);

    if (status === "loading") return <div className="p-8 text-sm text-[hsl(var(--mute))]" data-testid="wp13-companies-loading">Loading…</div>;
    if (status === "error") return <div className="p-8 text-sm" data-testid="wp13-companies-error">Companies could not be loaded{error ? ` (${error})` : ""}.</div>;

    const top10 = filtered.slice(0, 10);

    return (
        <div className="max-w-6xl space-y-6" data-testid="wp13-companies">
            <div>
                <h1 className="text-xl font-semibold" data-testid="wp13-companies-title">Companies</h1>
                <p className="text-[11.5px] text-[hsl(var(--mute))] mt-0.5">
                    Canonical companies observed across the market. <span className="num">{companies.length}</span> companies in the active publication.
                </p>
            </div>

            {/* ── Filters — dimensions are filters/context, never entity classes ─ */}
            <div className="card-flat p-3 flex items-center gap-2 flex-wrap" data-testid="wp13-companies-filters">
                <input value={search} onChange={(e) => { setSearch(e.target.value); setShown(50); }}
                       placeholder="Search companies…" data-testid="wp13-companies-search"
                       className="text-[11px] px-2.5 py-1.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface))] w-52" />
                <Select value={zone} onChange={(v) => { setZone(v); setShown(50); }}
                        options={zones.map((z) => ({ value: z.scopeKey, label: z.label }))} all="All market zones" testId="wp13-filter-zone" />
                <Select value={useCase} onChange={(v) => { setUseCase(v); setShown(50); }}
                        options={useCases.map((u) => ({ value: u.scopeKey, label: u.label }))} all="All use cases" testId="wp13-filter-usecase" />
                <Select value={chain} onChange={(v) => { setChain(v); setShown(50); }} options={facets.chains} all="All blockchains" testId="wp13-filter-chain" />
                <Select value={asset} onChange={(v) => { setAsset(v); setShown(50); }} options={facets.assets} all="All asset classes" testId="wp13-filter-asset" />
                <span className="text-[10px] text-[hsl(var(--mute))] ml-auto num" data-testid="wp13-companies-count">{filtered.length} companies</span>
            </div>

            {/* ── Observed activity over time (top 10 of current filter) ──────── */}
            {top10.length > 0 && (
                <section className="card-flat p-4 space-y-1" data-testid="wp13-companies-activity">
                    <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                        Observed activity over time
                        <InfoTip label="Observed activity" testId="wp13-tip-companies-activity">
                            Weekly published evidence activity for the ten most observed companies in the current filter. This shows observation coverage over time — it is not company strength, momentum, market share or importance.
                        </InfoTip>
                    </h2>
                    <p className="text-[10px] text-[hsl(var(--mute))]">Top {top10.length} companies of the current filter, by published evidence count.</p>
                    <ActivityChart companies={top10} />
                </section>
            )}

            {/* ── Most observed companies ─────────────────────────────────────── */}
            <section className="space-y-2" data-testid="wp13-companies-list">
                <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                    Most observed companies
                    <InfoTip label="Most observed" testId="wp13-tip-most-observed">
                        Ranked by published evidence count — a descriptive discovery aid showing how often a company appears in the published evidence. It is not a strength, leadership or importance ranking.
                    </InfoTip>
                </h2>
                <div className="card-flat overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] border-b border-[hsl(var(--line))]">
                                <th className="px-4 py-2 w-8">#</th>
                                <th className="px-2 py-2">Company</th>
                                <th className="px-2 py-2 text-right">Evidence</th>
                                <th className="px-2 py-2 text-right">Sources</th>
                                <th className="px-2 py-2 text-right">Patterns</th>
                                <th className="px-2 py-2 text-right">Weeks</th>
                                <th className="px-2 py-2 pr-4">Observed window</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.slice(0, shown).map((c, i) => (
                                <tr key={c.canonicalOrganizationId} className="border-b border-[hsl(var(--line))] last:border-0 hover:bg-[hsl(var(--surface-sunken))]/50">
                                    <td className="px-4 py-2 num text-[10px] text-[hsl(var(--mute))]">{i + 1}</td>
                                    <td className="px-2 py-2">
                                        <Link to={`/market-v2/organizations/${encodeURIComponent(c.canonicalOrganizationId)}`}
                                              className="text-[11.5px] font-medium hover:text-[hsl(var(--accent-ink))] hover:underline"
                                              data-testid={`wp13-company-link-${c.canonicalOrganizationId}`}>
                                            {c.displayName}
                                        </Link>
                                    </td>
                                    <td className="px-2 py-2 num text-[10px] text-right">{c.stats?.evidenceCountLifetime ?? "—"}</td>
                                    <td className="px-2 py-2 num text-[10px] text-right">{c.stats?.sourceCountLifetime ?? "—"}</td>
                                    <td className="px-2 py-2 num text-[10px] text-right">{c.stats?.linkedPatternCountLifetime ?? "—"}</td>
                                    <td className="px-2 py-2 num text-[10px] text-right">{c.stats?.weeksObserved ?? "—"}</td>
                                    <td className="px-2 py-2 pr-4 num text-[10px] text-[hsl(var(--mute))]">{c.stats?.firstSeenWeek} → {c.stats?.lastSeenWeek}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filtered.length > shown && (
                    <button type="button" onClick={() => setShown((s) => s + 100)} data-testid="wp13-companies-more"
                            className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline">
                        Show more ({filtered.length - shown} remaining)
                    </button>
                )}
            </section>
        </div>
    );
}
